/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

/**
 * String utils
 */
export class Strings {

  /**
   * Simple slugifier (doesn't account for accentued characters).
   *
   * From https://gist.github.com/mathewbyrne/1280286
   */
  public static slugify(text: string | number) {
    return text.toString().toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with -
      .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
      .replace(/\-\-+/g, '-')         // Replace multiple - with single -
      .replace(/^-+/, '')             // Trim - from start of text
      .replace(/-+$/, '');            // Trim - from end of text
  }

  public static toString(obj): string {
    if (!obj) {
      return '';
    }
    return obj.toString();
  }
}

export function replaceArray(replaceString: string, find: Array<string>, replace: string): string {
  for (let i = 0; i < find.length; i++) {
    replaceString = replaceString.replace(find[i], replace[i]);
  }
  return replaceString;
}

const CHARS_TO_ESCAPE = /["'();,=!~<>\s]/;

export const escapeValue = (value: string): string => {

  if (CHARS_TO_ESCAPE.test(value) || value.length === 0) {
    return value.replace(/"/g, '\\"').replace(/'/g, '\\\'');
  } else {
    return value;
  }
};

export const hasWhiteSpace = (str: string): boolean => /\s/g.test(str);

export const addQuotesToStringsWithWhiteSpace = (text: string): string =>
  hasWhiteSpace(text) ? '"' + text + '"' : text;


/**
 * Capitalizes the first character of the input string and lowercases the rest.
 * Ignores empty, null, undefined, or whitespace-only strings.
 *
 * @param text - The input string to format
 * @returns The formatted string with the first letter capitalized
 */
export const capitalize = (text: string): string => {
  if (typeof text !== 'string' || text.trim().length === 0) {
    return text;
  }
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Returns uppercase initials (max 2 characters) from a first name and last name,
 * or from a single full name string (split on space).
 *
 * @param firstName - First name or full name if lastName is omitted
 * @param lastName - Last name (optional)
 */
export const getInitials = (firstName?: string, lastName?: string): string => {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();

  if (!first && !last) {
    return '';
  }

  // If only firstName provided and it contains a space, treat it as "first last"
  if (first && !last && first.includes(' ')) {
    const parts = first.split(/\s+/);
    return ((parts[0]?.charAt(0) || '') + (parts[parts.length - 1]?.charAt(0) || '')).toUpperCase();
  }

  return ((first.charAt(0) || '') + (last.charAt(0) || '')).toUpperCase();
};

/**
 * Escapes HTML-special characters in a string to prevent XSS / broken markup
 * when interpolating user-provided text into HTML template literals.
 */
export const escapeHtml = (text: string): string => {
  if (!text) {
    return '';
  }
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Checks whether a photo URL is valid (non-null, non-undefined, and not containing
 * literal 'null' or 'undefined' path segments from the API).
 */
export const isValidPhotoUrl = (href: string): boolean => {
  return !!href && !/(null|undefined)$/.test(href);
};
